import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog } from './Dialog'
import { SearchPick } from './SearchPick'
import { matchOptions } from '../lib/searchPick'
import { useHubLang } from '../lib/i18n'
import { PHOTO_ROOM } from '../lib/hubCopy'
import type { VocabValue } from '../lib/photoRoom'
import {
  hashItems,
  itemsFromFiles,
  parseTaken,
  takenText,
  uploadToGallery,
  type GalleryItem,
  type GalleryProgress,
  type GalleryRun,
} from '../lib/gallery'

/**
 * 304 (FED-71). "Add photos" on the Gallery, for its admins: a file picker
 * that takes one photo or a folder of hundreds, and puts each straight into
 * the Gallery without a feed post (migration 304 says what that is).
 *
 * The shape, top to bottom: what happens (three sentences, once), the two
 * pickers and a drop zone, what the batch says about every photo (a typed
 * date for photos with no date of their own, a brand, tags, the file name as
 * caption), the list with a caption box per photo, and one button that
 * counts. Hundreds of rows render as rows; the thumbnails are object URLs
 * made when a row mounts and revoked when it unmounts.
 *
 * A tray (Dialog variant="tray"), like the contest Manage tray: a long list
 * belongs in a panel that scrolls, not in a box in the middle of the screen.
 *
 * Phases: pick → hashing (which are already here) → running → done. Stop
 * finishes the files in flight and leaves the rest `stopped`; picking the
 * same folder again skips everything that landed. Phase 2 (One reading what
 * was added) is reported but never blocks: the Gallery reads what it missed
 * the next time it opens (302).
 */
export function GalleryAddDialog({
  open,
  onClose,
  tenantId,
  personId,
  vocab,
  onAdded,
  initialItems,
}: {
  open: boolean
  onClose: () => void
  tenantId: string
  personId: string
  vocab: VocabValue[]
  /** Called once a run ends with at least one photo added; the page re-lists. */
  onAdded: (added: number) => void
  /** The harness only: rows to open with, so the list renders without a picker. */
  initialItems?: GalleryItem[]
}) {
  const lang = useHubLang()
  const t = PHOTO_ROOM[lang]
  const [items, setItems] = useState<GalleryItem[]>(initialItems ?? [])
  const [phase, setPhase] = useState<'pick' | 'hashing' | 'running' | 'done'>('pick')
  const [hashed, setHashed] = useState(0)
  const [progress, setProgress] = useState<GalleryProgress | null>(null)
  const [takenInput, setTakenInput] = useState('')
  const [brand, setBrand] = useState('')
  /* 321: the tag boxes narrow as you type; ticked ones always stay in view. */
  const [tagFilter, setTagFilter] = useState('')
  const [tagIds, setTagIds] = useState<Set<string>>(() => new Set())
  const [captionFromName, setCaptionFromName] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = useRef<GalleryRun | null>(null)
  // The run mutates the items in place; bump to re-render from them.
  const [, setRev] = useState(0)
  const rerender = () => setRev((n) => n + 1)

  const brands = useMemo(() => vocab.filter((v) => v.facet === 'brand' && !v.retired), [vocab])
  const tags = useMemo(() => vocab.filter((v) => v.facet !== 'brand' && !v.retired), [vocab])
  const label = (v: VocabValue) => (lang === 'es' ? v.labelEs : v.labelEn)

  const taken = takenInput.trim() ? parseTaken(takenInput) : null
  const takenBad = takenInput.trim() !== '' && taken === null

  const waiting = items.filter((i) => i.status === 'waiting').length
  const known = items.filter((i) => i.status === 'known').length
  const failed = items.filter((i) => i.status === 'failed').length
  const added = items.filter((i) => i.status === 'added').length

  async function addFiles(files: Iterable<File>) {
    setError(null)
    const fresh = await itemsFromFiles(files)
    if (fresh.length === 0) return
    setItems((cur) => [...cur, ...fresh])
    setPhase('hashing')
    try {
      await hashItems(fresh, (n) => setHashed(n))
    } catch (e) {
      setError(messageOf(e))
    }
    setPhase('pick')
    rerender()
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files
    if (list && list.length) void addFiles(Array.from(list))
    e.target.value = ''
  }

  async function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const files = await filesFromDrop(e.dataTransfer)
    if (files.length) void addFiles(files)
  }

  function start() {
    if (!tenantId || takenBad) return
    const valueIds = [...(brand ? [brand] : []), ...tagIds]
    setPhase('running')
    const r = uploadToGallery(
      items,
      { taken, valueIds, captionFromName },
      { tenantId, personId },
      (p) => {
        setProgress(p)
        rerender()
      },
    )
    run.current = r
    r.finished.then(() => {
      run.current = null
      setPhase('done')
      const n = items.filter((i) => i.status === 'added').length
      if (n > 0) onAdded(n)
    })
  }

  function stop() {
    run.current?.stop()
  }

  useEffect(() => () => run.current?.stop(), [])

  const busy = phase === 'running' || phase === 'hashing'

  return (
    <Dialog open={open} title={t.addTitle} onClose={onClose} variant="tray">
      <div className="galadd">
        {t.addIntro.map((para) => (
          <p key={para} className="galadd__intro">
            {para}
          </p>
        ))}

        {!busy && phase !== 'done' && (
          <div
            className={`galadd__drop${dragging ? ' is-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => void onDrop(e)}
          >
            <p className="galadd__dropline">{t.dropHere}</p>
            <div className="galadd__pickers">
              <label className="btn btn--primary btn--sm">
                {t.choosePhotos}
                <input type="file" className="sr-only" multiple accept="image/jpeg,image/png,image/webp" onChange={onPick} />
              </label>
              <label className="btn btn--quiet btn--sm">
                {t.chooseFolder}
                {/* webkitdirectory: Chrome, Edge, Safari and Firefox all take a folder with it. */}
                <input
                  type="file"
                  className="sr-only"
                  multiple
                  onChange={onPick}
                  {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                />
              </label>
            </div>
            <p className="galadd__note">{t.acceptNote}</p>
          </div>
        )}

        {phase === 'hashing' && (
          <p className="galadd__progress" role="status">
            {t.hashing(hashed, items.length)}
          </p>
        )}

        {items.length > 0 && phase !== 'done' && (
          <fieldset className="galadd__batch" disabled={busy}>
            <legend className="galadd__legend">{t.forEvery}</legend>
            <label className="galadd__field">
              <span>{t.takenLabel}</span>
              <input
                type="text"
                inputMode="numeric"
                value={takenInput}
                onChange={(e) => setTakenInput(e.target.value)}
                placeholder="1998 · 1998-06 · 1998-06-15"
                maxLength={10}
                aria-invalid={takenBad || undefined}
              />
              <small className={takenBad ? 'galadd__bad' : undefined}>{takenBad ? t.takenBad : t.takenHint}</small>
            </label>
            {brands.length > 0 && (
              <div className="galadd__field">
                <span>{t.brandLabel}</span>
                {/* 321: chosen by searching, like every tag (David, 29 Oct). */}
                {brand ? (
                  <span className="roomtag galadd__brand">
                    {label(brands.find((b) => b.id === brand) ?? brands[0])}
                    <button type="button" className="roomtag__x" aria-label={t.noBrand} title={t.noBrand} onClick={() => setBrand('')}>
                      ×
                    </button>
                  </span>
                ) : (
                  <SearchPick
                    options={brands.map((b) => ({ id: b.id, label: label(b) }))}
                    label={`${t.brandLabel}: ${t.brandPick}`}
                    placeholder={t.brandPick}
                    onPick={(o) => setBrand(o.id)}
                  />
                )}
              </div>
            )}
            {tags.length > 0 && (
              <div className="galadd__field">
                <span>{t.tagsLabel}</span>
                {tags.length > 8 && (
                  <input
                    type="search"
                    className="galadd__tagfilter"
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    placeholder={t.tagsFilter}
                    aria-label={t.tagsFilter}
                    maxLength={60}
                  />
                )}
                <div className="galadd__tags">
                  {(tagFilter.trim()
                    ? (() => {
                        const hit = new Set(matchOptions(tags.map((v) => ({ id: v.id, label: label(v) })), tagFilter, 500).map((o) => o.id))
                        return tags.filter((v) => hit.has(v.id) || tagIds.has(v.id))
                      })()
                    : tags
                  ).map((v) => (
                    <label key={v.id} className="galadd__tag">
                      <input
                        type="checkbox"
                        checked={tagIds.has(v.id)}
                        onChange={(e) => {
                          setTagIds((cur) => {
                            const next = new Set(cur)
                            if (e.target.checked) next.add(v.id)
                            else next.delete(v.id)
                            return next
                          })
                        }}
                      />{' '}
                      {label(v)}
                      <span className="galadd__facet">{t.facet[v.facet]}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <label className="galadd__check">
              <input type="checkbox" checked={captionFromName} onChange={(e) => setCaptionFromName(e.target.checked)} /> {t.captionFromName}
            </label>
          </fieldset>
        )}

        {items.length > 0 && (
          <ul className="galadd__list" aria-label={t.addTitle}>
            {items.map((it) => (
              <li key={it.id} className={`galadd__row galadd__row--${it.status}`}>
                <Thumb file={it.file} />
                <div className="galadd__meta">
                  <div className="galadd__name" title={it.name}>
                    {it.name}
                  </div>
                  <div className="galadd__facts">
                    {sizeOf(it.bytes)}
                    {' · '}
                    {it.exifTaken
                      ? t.exifTaken(takenText(it.exifTaken, 'day', lang))
                      : taken
                        ? t.willBeDated(takenText(taken.on, taken.precision, lang))
                        : t.noExif}
                  </div>
                  {phase !== 'done' && it.status === 'waiting' ? (
                    <input
                      type="text"
                      className="galadd__caption"
                      value={it.caption}
                      placeholder={t.captionPlaceholder}
                      maxLength={500}
                      disabled={busy}
                      onChange={(e) => {
                        it.caption = e.target.value
                        rerender()
                      }}
                    />
                  ) : it.caption ? (
                    <div className="galadd__facts">{it.caption}</div>
                  ) : null}
                </div>
                <div className={`galadd__status galadd__status--${it.status}`}>
                  {t.status[it.status]}
                  {it.error ? <small>{it.error}</small> : null}
                </div>
                {phase === 'pick' && it.status !== 'added' && (
                  <button
                    type="button"
                    className="galadd__remove"
                    aria-label={t.removeRow}
                    title={t.removeRow}
                    onClick={() => setItems((cur) => cur.filter((x) => x.id !== it.id))}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p className="photoroom__notice photoroom__notice--warn" role="alert">
            {error}
          </p>
        )}

        {progress && (
          <p className="galadd__progress" role="status">
            {phase === 'running'
              ? progress.reading
                ? t.reading(progress.reading.done, progress.reading.total)
                : t.adding(progress.done, progress.total)
              : t.summary(added, known, failed)}
            {phase === 'done' && items.some((i) => i.status === 'stopped') ? ` ${t.stopped}` : ''}
          </p>
        )}

        <div className="galadd__actions">
          {phase === 'pick' && (
            <button type="button" className="btn btn--primary btn--sm" disabled={waiting === 0 || takenBad} onClick={start}>
              {t.addCount(waiting)}
            </button>
          )}
          {phase === 'running' && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={stop} disabled={!!progress?.reading}>
              {t.stop}
            </button>
          )}
          {phase === 'done' && failed > 0 && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                setPhase('pick')
                setProgress(null)
              }}
            >
              {t.retryFailed}
            </button>
          )}
          <button type="button" className="btn btn--quiet btn--sm" onClick={onClose}>
            {t.close}
          </button>
          {items.length > 0 && phase === 'pick' && (
            <span className="galadd__counts">{t.counts(waiting, known, failed)}</span>
          )}
        </div>
      </div>
    </Dialog>
  )
}

/** The picked file, as a thumbnail: an object URL for the row's life only. */
function Thumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file])
  return url ? <img className="galadd__thumb" src={url} alt="" loading="lazy" /> : <span className="galadd__thumb" aria-hidden="true" />
}

function sizeOf(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`
  return `${bytes} B`
}

function messageOf(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message
  }
  return String(e)
}

type EntryLike = {
  isFile: boolean
  isDirectory: boolean
  file: (ok: (f: File) => void, err: (e: unknown) => void) => void
  createReader: () => { readEntries: (ok: (es: EntryLike[]) => void, err: (e: unknown) => void) => void }
}

/**
 * A dropped folder arrives as a directory entry, not as files. Walk it
 * (readEntries returns in batches until empty). A plain file list is just
 * the files.
 */
async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const out: File[] = []
  const entries: EntryLike[] = []
  for (const item of Array.from(dt.items ?? [])) {
    const get = (item as unknown as { webkitGetAsEntry?: () => EntryLike | null }).webkitGetAsEntry
    const entry = get ? get.call(item) : null
    if (entry) entries.push(entry)
  }
  if (entries.length === 0) return Array.from(dt.files ?? [])
  async function walk(entry: EntryLike): Promise<void> {
    if (entry.isFile) {
      const f = await new Promise<File | null>((resolve) => entry.file(resolve, () => resolve(null)))
      if (f) out.push(f)
      return
    }
    if (!entry.isDirectory) return
    const reader = entry.createReader()
    for (;;) {
      const batch = await new Promise<EntryLike[]>((resolve) => reader.readEntries(resolve, () => resolve([])))
      if (batch.length === 0) break
      for (const e of batch) await walk(e)
    }
  }
  for (const e of entries) await walk(e)
  return out
}
